import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowLeft, Info, ShieldCheck, CheckCircle2, Loader2, Lock } from "lucide-react";
import Logo from "@/components/Logo";
import OtpModal from "@/pages/OtpModal";
import { saveLead } from "@/lib/api";
import { resetOperation } from "@/lib/api";
import { toast } from "sonner";
import {
  AVISO_INICIAL, AVISO_INTERMEDIACION, MODALIDADES, TIPOS_VEHICULO,
  INGRESOS, FUENTES_INGRESO, ANTIGUEDAD, ENGANCHES, HISTORIAL, EDADES, isContado,
} from "@/data/formConfig";
import { PRIVACIDAD_SIMPLIFICADO } from "@/data/legal";

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const phoneOk = (v) => (v || "").replace(/\D/g, "").length >= 10;

function Label({ children, id, opcional }) {
  return (
    <label htmlFor={id} className="block text-white font-medium mb-2">
      {children} {opcional && <span className="text-white/45 font-normal text-sm">(opcional)</span>}
    </label>
  );
}

function Select({ id, value, onChange, options, placeholder, testId }) {
  return (
    <select id={id} data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
      className="af-input" style={{ appearance: "none", backgroundImage: "none" }}>
      <option value="" disabled>{placeholder || "Selecciona una opción"}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

const Block = ({ title, children }) => (
  <div className="af-card p-6 md:p-8">
    <h3 className="h-impact text-white text-xl md:text-2xl mb-6">{title}</h3>
    <div className="space-y-6">{children}</div>
  </div>
);

export default function Registro() {
  const navigate = useNavigate();
  const location = useLocation();
  const preVehiculo = location.state?.vehiculo || "";

  const [f, setF] = useState({
    q01_nombre: "", q02_email: "", q03_celular: "",
    q04_modalidad: "", q05_tipo_vehiculo: "", q06_vehiculo_interes: preVehiculo,
    q07_ingreso: "", q08_fuente_ingreso: "", q09_antiguedad: "", q10_enganche: "",
    q11_historial_crediticio: "", q12_rango_edad: "",
  });
  const [c, setC] = useState({ terminos: false, marketing: false, declaraBuro: false });
  const [submitting, setSubmitting] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const contado = isContado(f.q04_modalidad);
  const showResto = f.q04_modalidad && !contado; // Crédito / Arrendamiento / Aún no estoy seguro

  useEffect(() => { if (preVehiculo) setF((s) => ({ ...s, q06_vehiculo_interes: preVehiculo })); }, [preVehiculo]);
  useEffect(() => { resetOperation(); }, []);

  // Campos obligatorios de la ruta actual (Q06 nunca cuenta)
  const requiredKeys = useMemo(() => {
    if (!f.q04_modalidad) return ["q01_nombre", "q02_email", "q03_celular", "q04_modalidad"];
    if (contado) return ["q01_nombre", "q02_email", "q03_celular", "q04_modalidad"];
    return ["q01_nombre", "q02_email", "q03_celular", "q04_modalidad", "q05_tipo_vehiculo",
      "q07_ingreso", "q08_fuente_ingreso", "q09_antiguedad", "q10_enganche",
      "q11_historial_crediticio", "q12_rango_edad"];
  }, [f.q04_modalidad, contado]);

  const isFilled = (k) => {
    const v = f[k];
    if (!v) return false;
    if (k === "q02_email") return emailOk(v);
    if (k === "q03_celular") return phoneOk(v);
    return true;
  };

  const completed = requiredKeys.filter(isFilled).length;
  const total = contado ? 4 : (f.q04_modalidad ? 11 : 4);

  const declaraOk = contado ? true : c.declaraBuro; // declaración informativa solo en ruta crédito/arrendamiento
  const allRequiredFilled = requiredKeys.every(isFilled);
  const canSubmit = allRequiredFilled && c.terminos && declaraOk && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await saveLead({
        ...f,
        consents: {
          acepta_terminos: c.terminos,
          acepta_marketing: c.marketing,
          reconoce_intermediacion: c.terminos,
          declara_informativa_buro: !!c.declaraBuro,
          autoriza_buro: false,
        },
      });
      setOtpOpen(true);
    } catch (e) {
      toast.error("No pudimos guardar tu información en este momento. Intenta de nuevo en unos minutos.");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--af-black)" }}>
        <div className="af-card p-10 max-w-lg text-center" data-testid="registro-confirmacion">
          <CheckCircle2 className="text-af-orange-core mx-auto mb-5" size={64} />
          <h2 className="h-impact text-white text-2xl md:text-3xl">¡Listo!</h2>
          <p className="text-white/75 mt-4 leading-relaxed">
            Ya tenemos tu información. En la siguiente fase de la plataforma podrás ver aquí las opciones que mejor se ajustan a tu perfil.
          </p>
          <button data-testid="confirm-volver" onClick={() => navigate("/")} className="af-btn-primary mt-8">Volver al inicio</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--af-black)" }}>
      {/* top bar */}
      <div className="sticky top-0 z-40" style={{ background: "rgba(13,13,13,0.95)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--af-border-dark)" }}>
        <div className="af-container flex items-center justify-between" style={{ height: 68 }}>
          <button data-testid="registro-back" onClick={() => navigate("/")} className="flex items-center gap-2 text-white/80 hover:text-af-orange-core">
            <ArrowLeft size={20} /> <Logo height={40} />
          </button>
          <div className="text-white/70 text-sm hidden sm:block">Precalificación gratuita</div>
        </div>
      </div>

      <div className="af-container py-8 md:py-12 max-w-3xl">
        {/* Aviso inicial obligatorio */}
        <div className="af-card p-5 mb-4" style={{ borderColor: "rgba(232,93,4,0.4)" }} data-testid="aviso-inicial">
          <div className="flex gap-3">
            <Info className="text-af-orange-core shrink-0 mt-0.5" size={20} />
            <p className="text-white/85 text-sm leading-relaxed">{AVISO_INICIAL}</p>
          </div>
        </div>

        {/* Aviso intermediación */}
        <div className="text-white/55 text-xs mb-6 leading-relaxed" data-testid="aviso-intermediacion">{AVISO_INTERMEDIACION}</div>

        <h1 className="h-impact text-white text-3xl md:text-4xl mb-2">Cuéntanos sobre ti</h1>
        <p className="text-white/60 mb-6">Responde el cuestionario para ver tus alternativas.</p>

        {/* Progress */}
        <div className="af-card p-4 mb-8 sticky top-[76px] z-30" data-testid="progress-indicator">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-white/70">Progreso</span>
            <span className="text-af-orange-core font-semibold">{Math.min(completed, total)} de {total} campos obligatorios completados</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "#111" }}>
            <div className="h-full transition-all duration-300" style={{ width: `${total ? (Math.min(completed, total) / total) * 100 : 0}%`, background: "var(--af-orange-deep)" }} />
          </div>
        </div>

        <div className="space-y-6">
          {/* Bloque 1 */}
          <Block title="Datos generales">
            <div>
              <Label id="q01">Nombre completo</Label>
              <input id="q01" data-testid="q01-nombre" className="af-input" value={f.q01_nombre}
                onChange={(e) => set("q01_nombre")(e.target.value)} placeholder="Ej. Ana Pérez López" />
            </div>
            <div>
              <Label id="q02">Correo electrónico</Label>
              <input id="q02" data-testid="q02-email" type="email" className={`af-input ${f.q02_email && !emailOk(f.q02_email) ? "invalid" : ""}`}
                value={f.q02_email} onChange={(e) => set("q02_email")(e.target.value)} placeholder="tucorreo@ejemplo.com" />
              {f.q02_email && !emailOk(f.q02_email) && <div className="text-red-400 text-xs mt-1">Ingresa un correo válido.</div>}
            </div>
            <div>
              <Label id="q03">Número celular</Label>
              <input id="q03" data-testid="q03-celular" type="tel" className={`af-input ${f.q03_celular && !phoneOk(f.q03_celular) ? "invalid" : ""}`}
                value={f.q03_celular} onChange={(e) => set("q03_celular")(e.target.value)} placeholder="10 dígitos (México)" />
              {f.q03_celular && !phoneOk(f.q03_celular) && <div className="text-red-400 text-xs mt-1">Ingresa un celular de al menos 10 dígitos.</div>}
            </div>
          </Block>

          {/* Bloque 2 */}
          <Block title="Modalidad y vehículo">
            <div>
              <Label id="q04">¿Cómo te gustaría adquirirlo?</Label>
              <Select id="q04" testId="q04-modalidad" value={f.q04_modalidad} onChange={set("q04_modalidad")} options={MODALIDADES} />
            </div>
            {showResto && (
              <div data-testid="q05-wrapper">
                <Label id="q05">¿Qué tipo de vehículo estás buscando?</Label>
                <Select id="q05" testId="q05-tipo" value={f.q05_tipo_vehiculo} onChange={set("q05_tipo_vehiculo")} options={TIPOS_VEHICULO} />
              </div>
            )}
            <div>
              <Label id="q06" opcional>¿Qué vehículo te gustaría?</Label>
              <input id="q06" data-testid="q06-vehiculo" className="af-input" value={f.q06_vehiculo_interes}
                onChange={(e) => set("q06_vehiculo_interes")(e.target.value)} placeholder="Ej. Mazda CX-5" />
              <div className="text-white/45 text-xs mt-1">Opcional — nos ayuda a orientarte mejor.</div>
            </div>
          </Block>

          {/* Bloques 3 y 4 (solo ruta crédito / arrendamiento / aún no estoy seguro) */}
          {showResto && (
            <>
              <Block title="Perfil económico">
                <div>
                  <Label id="q07">¿Cuál es tu ingreso mensual aproximado?</Label>
                  <Select id="q07" testId="q07-ingreso" value={f.q07_ingreso} onChange={set("q07_ingreso")} options={INGRESOS} />
                </div>
                <div>
                  <Label id="q08">¿Cómo obtienes principalmente tus ingresos?</Label>
                  <Select id="q08" testId="q08-fuente" value={f.q08_fuente_ingreso} onChange={set("q08_fuente_ingreso")} options={FUENTES_INGRESO} />
                </div>
                <div>
                  <Label id="q09">¿Cuánto tiempo llevas obteniendo ingresos de esa fuente?</Label>
                  <Select id="q09" testId="q09-antiguedad" value={f.q09_antiguedad} onChange={set("q09_antiguedad")} options={ANTIGUEDAD} />
                </div>
                <div>
                  <Label id="q10">¿Qué enganche podrías aportar?</Label>
                  <Select id="q10" testId="q10-enganche" value={f.q10_enganche} onChange={set("q10_enganche")} options={ENGANCHES} />
                </div>
              </Block>

              <Block title="Perfil crediticio y demográfico">
                {/* Declaración informativa antes de Q11 (obligatoria en esta ruta) */}
                <label className="flex items-start gap-3 p-4 rounded-lg cursor-pointer" style={{ background: "#111", border: "1px solid var(--af-border-dark)" }}>
                  <input data-testid="declara-buro" type="checkbox" className="mt-1" checked={c.declaraBuro}
                    onChange={(e) => setC((s) => ({ ...s, declaraBuro: e.target.checked }))} />
                  <span className="text-white/80 text-sm leading-relaxed">
                    Esta es una declaración informativa de tu parte, no una consulta formal a Buró de Crédito. Cualquier consulta formal posterior requerirá tu autorización explícita por separado.
                  </span>
                </label>
                <div>
                  <Label id="q11">¿Cómo describirías tu historial crediticio?</Label>
                  <Select id="q11" testId="q11-historial" value={f.q11_historial_crediticio} onChange={set("q11_historial_crediticio")} options={HISTORIAL} />
                </div>
                <div>
                  <Label id="q12">¿Cuál es tu rango de edad?</Label>
                  <Select id="q12" testId="q12-edad" value={f.q12_rango_edad} onChange={set("q12_rango_edad")} options={EDADES} />
                </div>
              </Block>
            </>
          )}

          {/* Aviso de privacidad simplificado */}
          <div className="af-card p-5 text-xs text-white/55 leading-relaxed" data-testid="aviso-simplificado">
            <span className="text-white/75 font-medium">{PRIVACIDAD_SIMPLIFICADO.title}: </span>
            {PRIVACIDAD_SIMPLIFICADO.text}{" "}
            <Link to="/legal/privacidad" className="text-af-orange-core underline">Ver Aviso de Privacidad Integral</Link>.
          </div>

          {/* Consentimientos */}
          <div className="af-card p-6 space-y-4">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-terminos-wrapper">
              <input data-testid="consent-terminos" type="checkbox" className="mt-1" checked={c.terminos}
                onChange={(e) => setC((s) => ({ ...s, terminos: e.target.checked }))} />
              <span className="text-white/80 text-sm leading-relaxed">
                Acepto los <Link to="/legal/terminos" target="_blank" className="text-af-orange-core underline">Términos y Condiciones</Link> y reconozco el <Link to="/legal/privacidad" target="_blank" className="text-af-orange-core underline">Aviso de Privacidad Integral</Link>.
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-marketing-wrapper">
              <input data-testid="consent-marketing" type="checkbox" className="mt-1" checked={c.marketing}
                onChange={(e) => setC((s) => ({ ...s, marketing: e.target.checked }))} />
              <span className="text-white/80 text-sm leading-relaxed">Deseo recibir ofertas, promociones y boletines comerciales.</span>
            </label>
          </div>

          {/* Submit */}
          <button data-testid="ver-mis-opciones" onClick={handleSubmit} disabled={!canSubmit}
            className="af-btn-primary w-full" style={{ padding: "1.1rem", fontSize: "1.1rem" }}>
            {submitting ? <><Loader2 className="animate-spin" size={20} /> Guardando…</> : <><ShieldCheck size={20} /> Ver mis opciones</>}
          </button>
          {!canSubmit && (
            <p className="text-white/45 text-xs text-center">
              Completa los campos obligatorios{!c.terminos ? " y acepta los Términos y Condiciones" : ""}{!declaraOk ? " y la declaración informativa" : ""} para continuar.
            </p>
          )}
          <div className="flex items-center justify-center gap-2 text-white/50 text-xs">
            <Lock size={14} className="text-af-orange-core" /> Tu información está protegida y no representa una aprobación definitiva.
          </div>
        </div>
      </div>

      <OtpModal
        open={otpOpen}
        phone={f.q03_celular}
        onClose={() => setOtpOpen(false)}
        onVerified={() => { setOtpOpen(false); navigate("/resultados"); }}
      />
    </div>
  );
}
